import { SmartFormFieldsInterface } from '@app/components/forms/smartFormFields.vue';
import { ILoginButton, ISSO, useLogin } from '@app/components/login/useLogin';
import { useValidation, type ValidatedFieldInterface } from '@ui/composables/useValidation';
import { useFetchRecruitis } from '@/js/utils/useFetchApi';
import { AlertInterface } from '@/js/UI-SHARED/components/alerts/alert.vue';
import { useField } from '@utils/field';
import { useDebounceFn } from '@vueuse/core';
import { flashMessage } from '@utils/flashMessage';
import { FlashMessageThemeEnum } from '@/js/enums/FlashMessageThemeEnum';
import { translateAsync } from '@utils/I18n';

export type LoginPageProps = {
    sections: LoginSectionDefinitions,
    showSection: StepType,
    alert?: AlertInterface | null,
    workspace?: {
        name: string,
        companyId: number,
    } | null,
    redirectToken?: string | null,
};

export type LoginSectionDefinitions = {
    login: {
        form: SmartFormFieldsInterface;
        buttons: ILoginButton[];
    },
    twoFA: {
        form: SmartFormFieldsInterface;
        buttons: ILoginButton[];
    },
    lostPassword: {
        form: SmartFormFieldsInterface;
        buttons: ILoginButton[];
    },
    sso: {
        form: SmartFormFieldsInterface,
        buttons: ISSO[],
        formButtons: ILoginButton[],
    },
    resetPassword: {
        form: SmartFormFieldsInterface;
        buttons: ILoginButton[];
    }
};

const {
    onFormCallback,
    setLoadingIndicator,
    updateForm,
    updateSso,
    onSsoCallback,
    removeSso,
    updateAlerts,
    removeAlerts,
    updateButtons,
    onButtonCallback,
    validateForm,
    removeWorkspace,
    updateWorkspace,
    removeFormFields,
    addAlert,
    form,
} = useLogin();

const {
    findFieldByName,
} = useField();

type StepType = 'login' | 'twoFA' | 'lostPassword' | 'resetPassword';

const URLS: Record<StepType, string> = {
    login: '/auth/login/form-submit',
    twoFA: '/auth/login/code-verify',
    lostPassword: '/auth/login/password-recovery',
    resetPassword: '/auth/login/password-reset',
};

const SSO_PROVIDERS: Record<string, { path: string, requiresCompany: boolean }> = {
    google: { path: 'google', requiresCompany: false },
    microsoft: { path: 'azure', requiresCompany: false },
    decathlon: { path: 'decathlon', requiresCompany: false },
    adfs: { path: 'adfs', requiresCompany: true },
};

let loginSections: LoginSectionDefinitions;
let currentStep: StepType = 'login';
let currentPayload: Record<string, string> = null;
let currentCompanyId: number | null = null;
let redirectToken: string | null = null;

const checkPasswordStrength = async (password: string, passwordMeter: ValidatedFieldInterface) => {
    const response = await fetch('/public/security/password-score', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
    });

    const data = await response.json();

    passwordMeter.initialValue = data.score;
};

const checkPasswordStrengthDebounced = useDebounceFn(checkPasswordStrength, 300);

const trimFieldValues = (fields: ValidatedFieldInterface[]) => {
    const { setValidationMessage } = useValidation(form.value);

    for (const field of fields) {
        if (field.group) {
            trimFieldValues(field.group);
        }
        if (typeof field.initialValue === 'string' && field.initialValue !== field.initialValue.trim()) {
            field.initialValue = field.initialValue.trim();
            // Field updates have already been validated, so refresh errors after trimming.
            setValidationMessage(field, field);
        }
    }
};

export class AuthManager {
    constructor(loginPageProps: LoginPageProps) {
        loginSections = loginPageProps.sections;
        redirectToken = loginPageProps.redirectToken ?? null;

        this.setCurrentStep(loginPageProps.showSection);
        updateSso(loginSections.sso.buttons);

        if (loginPageProps.alert) {
            addAlert(loginPageProps.alert);
        }

        if (loginPageProps.workspace) {
            currentCompanyId = loginPageProps.workspace.companyId;
            updateWorkspace({ name: loginPageProps.workspace.name });
            removeFormFields();
            updateButtons(loginSections.sso.formButtons);
        }
    }

    init() {
        onFormCallback(async (callback) => {
            if (callback.action === 'submit') {
                this.onSubmit();
            } else if (callback.action === 'validate') {
                return;
            } else {

                const changedField = (callback as any).changedField as ValidatedFieldInterface;
                const fields = (callback as any).updatedFieldOrFields as ValidatedFieldInterface[];

                trimFieldValues(fields);

                const passwordInput = findFieldByName(fields, 'password');
                const passwordMeter = findFieldByName(fields, 'passwordStrength');
                const passwordShowButton = findFieldByName(fields, 'revealPassword');

                const passwordDidChanged = changedField?.name === passwordInput?.name;

                if (passwordInput && passwordMeter && passwordDidChanged) {
                    checkPasswordStrengthDebounced(passwordInput.initialValue, passwordMeter);
                }

                if (passwordShowButton && changedField?.name === passwordShowButton.name) {
                    passwordShowButton.props.icon = passwordShowButton.props.icon === 'far fa-eye'
                        ? 'far fa-eye-slash'
                        : 'far fa-eye';
                    passwordInput.type = passwordInput.type === 'password' ? 'input' : 'password';
                }

                updateForm({
                    fields,
                });
            }
        });
        onSsoCallback((sso: ISSO) => {
            this.handleSsoClick(sso);
        });
        onButtonCallback((action: ILoginButton) => {
            switch (action.name) {
                case 'lostPassword':
                    this.handleLostPassButton();
                    break;
                case 'retry2FA':
                    currentStep = 'login';
                    this.onSubmit(currentPayload);
                    break;
                case 'back':
                    this.handleBackButton();
                    break;
                case 'ssoSubmit':
                    this.handleWorkspaceSubmit();
                    break;
            }
        });
    }

    extractFieldValues(fields: ValidatedFieldInterface[]): Record<string, string> {
        const userField = findFieldByName(fields, 'username');
        const passField = findFieldByName(fields, 'password');
        const passAgainField = findFieldByName(fields, 'passwordAgain');
        const resetHashField = findFieldByName(fields, 'resetHash');
        const codeField = findFieldByName(fields, 'code');
        const emailField = findFieldByName(fields, 'lostPassword');
        const ssoField = findFieldByName(fields, 'ssoName');

        const username = userField ? userField.initialValue : '';
        const password = passField ? passField.initialValue : '';
        const passwordAgain = passAgainField ? passAgainField.initialValue : '';
        const resetHash = resetHashField ? resetHashField.initialValue : '';
        const code = codeField ? codeField.initialValue : '';
        const email = emailField ? emailField.initialValue : '';
        const ssoName = ssoField ? ssoField.initialValue : '';

        switch (currentStep) {
            case 'login': return { username, password, ...(redirectToken ? { redirectToken } : {}) };
            case 'twoFA': return { code, ...(redirectToken ? { redirectToken } : {}) };
            case 'lostPassword': return { email };
            case 'resetPassword': return { password, passwordAgain, resetHash };
            default: throw new Error(`step "${currentStep}" not implemented`);
        }
    }

    async onSubmit(validPayload?: Record<string, string>) {
        if (form.value.loading) {
            return;
        }
        removeAlerts();

        if (!validPayload) {
            await validateForm();

            const hasErrorMessage = form.value.fields.some((field) => field.message?.show === true);
            if (hasErrorMessage) {
                return;
            }

            currentPayload = this.extractFieldValues(form.value.fields);

            if (currentPayload.resetHash && currentPayload.password !== currentPayload.passwordAgain) {
                addAlert({
                    name: 'password-mismatch',
                    type: 'error',
                    text: await translateAsync('account.reset.form.password_not_same'),
                });
                return;
            }
        }

        setLoadingIndicator(true);

        const { data, statusCode, error } = await useFetchRecruitis(URLS[currentStep], {})
            .post(currentPayload)
            .json<{
            redirect?: string,
            alert?: AlertInterface,
            step?: StepType,
            provider?: string,
            redirectToken?: string,
        }>();

        if (statusCode.value === 400) {
            flashMessage(FlashMessageThemeEnum.Error, error.value.payload.message);
            for (const [field, message] of Object.entries(error.value.payload.violations)) {
                const formField = findFieldByName(form.value.fields, field);
                if (formField) {
                    formField.message = {
                        type: 'error',
                        text: message as string,
                        show: true,
                    };
                }
            }

            setLoadingIndicator(false);
            return;
        }

        if (data.value.redirect) {
            window.location.href = data.value.redirect;
            return;
        }

        setLoadingIndicator(false);

        if (data.value.redirectToken) {
            redirectToken = data.value.redirectToken;
        }

        if (data.value.step) {
            this.setCurrentStep(data.value.step);
        }

        if (data.value.alert) {
            addAlert(data.value.alert);
        }
    }

    setSteptwoFA() {
        this.setCurrentStep('twoFA');
        removeSso();
    }

    handleBackButton() {
        if (currentCompanyId !== null) {
            const rtParam = redirectToken ? `&rt=${encodeURIComponent(redirectToken)}` : '';
            window.location.href = `/auth/login?reset_workspace${rtParam}`;
            return;
        }
        this.setCurrentStep('login');
        removeWorkspace();
        currentCompanyId = null;
        updateSso(loginSections.sso.buttons);
    }

    handleLostPassButton() {
        this.setCurrentStep('lostPassword');
        removeSso();
    }

    handleSsoClick(sso: ISSO) {
        const rtParam = redirectToken ? `?rt=${encodeURIComponent(redirectToken)}` : '';

        const provider = Object.prototype.hasOwnProperty.call(SSO_PROVIDERS, sso.name)
            ? SSO_PROVIDERS[sso.name]
            : undefined;
        if (provider) {
            if (provider.requiresCompany && currentCompanyId === null) {
                return;
            }

            const url = currentCompanyId
                ? `/auth/sso/${provider.path}/${currentCompanyId}`
                : `/auth/sso/${provider.path}`;
            window.location.href = url + rtParam;
            return;
        }

        if (sso.name === 'sso') {
            this.showWorkspaceInput();
        }
    }

    showWorkspaceInput() {
        removeAlerts();
        updateForm(loginSections.sso.form);
        updateButtons(loginSections.sso.formButtons);
        removeSso();
    }

    async handleWorkspaceSubmit() {
        removeAlerts();
        await validateForm();

        const hasErrorMessage = form.value.fields.some((field) => field.message?.show === true);
        if (hasErrorMessage) {
            return;
        }

        const ssoField = findFieldByName(form.value.fields, 'ssoName');
        const workspaceName = ssoField ? ssoField.initialValue : '';
        if (workspaceName === '') {
            return;
        }

        setLoadingIndicator(true);
        const rtParam = redirectToken ? `?rt=${encodeURIComponent(redirectToken)}` : '';
        window.location.href = `/auth/login/${encodeURIComponent(workspaceName)}${rtParam}`;
    }

    setCurrentStep(step: StepType) {
        currentStep = step;
        removeAlerts();
        updateForm(loginSections[currentStep].form);
        updateButtons(loginSections[currentStep].buttons);
    }
}
